/**
 * The posting, read here -- against the record.
 *
 * One button on a card opens the employer's own text in place, with the
 * seeker's terms it uses marked solid and the distinctive terms it asks for
 * that the resume lacks marked dashed. The reading is the reasoning: what
 * fits and what is missing are in the text, not summarised beside it. The
 * text is never rewritten; the marks are the same words the scorer counted.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { segments, lines, dateWords } from "./posting";

export interface PostingView {
  id: string;
  title: string;
  employer: string;
  place: string | null;
  isRemote: boolean;
  postedAt: string | null;
  closedAt: string | null;
  lastSeenAt: string | null;
  applyUrl: string | null;
  comp: { minCents: number | null; maxCents: number | null } | null;
  text: string;
}

interface Reading {
  posting: PostingView;
  have: string[];
  lack: string[];
}

/** Read once per posting per page load; opening a card again is instant. */
const cache = new Map<string, Promise<Reading>>();

function read(jobId: string): Promise<Reading> {
  let p = cache.get(jobId);
  if (!p) {
    p = api<Reading>(`/api/jobs/${jobId}`);
    p.catch(() => cache.delete(jobId));
    cache.set(jobId, p);
  }
  return p;
}

function money(cents: number | null): string | null {
  if (cents === null) return null;
  return "$" + Math.round(cents / 100).toLocaleString();
}

function payWords(comp: PostingView["comp"]): string | null {
  if (!comp) return null;
  const lo = money(comp.minCents);
  const hi = money(comp.maxCents);
  if (lo && hi && lo !== hi) return `${lo}–${hi}`;
  return lo ?? hi;
}

/** Lines that are too long to be a heading fold at first; the rest of the posting is a click away. */
const FOLD_AFTER = 14;

export function PostingReader({ jobId }: { jobId: string }) {
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState<Reading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [whole, setWhole] = useState(false);

  useEffect(() => {
    if (!open || reading) return;
    let live = true;
    setError(null);
    read(jobId)
      .then((r) => { if (live) setReading(r); })
      .catch((e) => { if (live) setError(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message); });
    return () => { live = false; };
  }, [open, reading, jobId]);

  return (
    <div className="posting">
      <button className="step-link" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Hide the posting" : "Read the posting"}
      </button>

      {open && !reading && !error && <p className="muted small">Reading…</p>}
      {open && error && <p className="err small">{error}</p>}

      {open && reading && (() => {
        const { posting, have, lack } = reading;
        const all = lines(posting.text);
        const shown = whole ? all : all.slice(0, FOLD_AFTER);
        const facts = [
          posting.postedAt ? `posted ${dateWords(posting.postedAt)}` : null,
          posting.closedAt ? `closed ${dateWords(posting.closedAt)}` : posting.lastSeenAt ? `listed ${dateWords(posting.lastSeenAt)}` : null,
          posting.isRemote ? "remote" : posting.place,
          payWords(posting.comp),
        ].filter(Boolean).join(" · ");
        const marked = have.length > 0 || lack.length > 0;
        return (
          <div className="posting-body">
            {facts && <p className="muted small posting-facts">{facts}</p>}
            {marked ? (
              <p className="muted small posting-key">
                Marked: {have.length > 0 && <><mark className="have">{have.length === 1 ? "1 word" : `${have.length} words`} of yours</mark> it uses</>}
                {have.length > 0 && lack.length > 0 && ", and "}
                {lack.length > 0 && <><mark className="lack">{lack.length === 1 ? "1 word" : `${lack.length} words`}</mark> it asks for that your resume does not say</>}
                . The text itself is the employer's, untouched.
              </p>
            ) : (
              <p className="muted small posting-key">Nothing is marked: there is no confirmed resume to read this against yet.</p>
            )}
            {all.length === 0 ? (
              <p className="muted small">The board did not publish a description for this one.</p>
            ) : (
              <div className="posting-text">
                {shown.map((line, i) => (
                  <p key={i}>
                    {segments(line, have, lack).map((s, j) =>
                      s.mark ? <mark key={j} className={s.mark}>{s.text}</mark> : <span key={j}>{s.text}</span>,
                    )}
                  </p>
                ))}
              </div>
            )}
            {all.length > FOLD_AFTER && (
              <button className="step-link small" onClick={() => setWhole(!whole)}>
                {whole ? "Show less" : `Show the whole posting (${all.length - FOLD_AFTER} more lines)`}
              </button>
            )}
            {posting.applyUrl && (
              <p className="small posting-source">
                <a href={posting.applyUrl} target="_blank" rel="noreferrer noopener">Open it on the employer's site</a>
              </p>
            )}
          </div>
        );
      })()}
    </div>
  );
}
